import React, { useState, useEffect } from 'react';
import { useHeader } from '../Layout/MainLayout';
import SpecActionSheet, { ActionSheetItem } from '../SpecActionSheet/SpecActionSheet';
import { PlayButtonXL } from '../PlayButton/PlayButtonXL';
import { useAudioPlayer } from '../../contexts/AudioPlayerContext';
import './Profile.css';

export type ProfileType = 'musician' | 'fan' | 'band' | 'venue';
export type HeroVariant = 'blurred' | 'simple' | 'map' | 'none';

export interface ProfileLayoutProps {
  profileType: ProfileType;
  heroVariant: HeroVariant;
  heroImageUrl?: string;
  showPlayButton?: boolean;
  soundcloudUrl?: string;
  actionSheetItems: ActionSheetItem[];
  children: React.ReactNode;
}

export const ProfileLayout: React.FC<ProfileLayoutProps> = ({
  heroVariant,
  heroImageUrl,
  showPlayButton = false,
  soundcloudUrl,
  actionSheetItems,
  children,
}) => {
  const [actionSheetOpen, setActionSheetOpen] = useState(false);
  const { setOnMoreClick } = useHeader();

  // Audio player context
  const { state, toggle, getProgress } = useAudioPlayer();

  const isPlaying = soundcloudUrl ? state.currentUrl === soundcloudUrl && state.isPlaying : false;
  const isLoading = soundcloudUrl ? state.currentUrl === soundcloudUrl && state.isLoading : false;
  const progress = soundcloudUrl ? getProgress(soundcloudUrl) : 0;

  useEffect(() => {
    setOnMoreClick(() => setActionSheetOpen(true));
    return () => {
      setOnMoreClick(undefined);
    };
  }, [setOnMoreClick]);

  const handlePlayToggle = () => {
    if (soundcloudUrl) {
      toggle(soundcloudUrl);
    }
  };

  // Only show play button if there's a soundcloud URL and showPlayButton is true
  const shouldShowPlayButton = showPlayButton && !!soundcloudUrl;

  return (
    <div className="profile-page">
      {heroVariant === 'blurred' && heroImageUrl && (
        <div className="profile-page__hero">
          <div
            className="profile-page__hero-bg"
            style={{ backgroundImage: `url("${heroImageUrl}")` }}
          />
          <div className="profile-page__hero-overlay" />
          {shouldShowPlayButton && (
            <div className="profile-page__play-button">
              <PlayButtonXL
                isPlaying={isPlaying}
                isLoading={isLoading}
                progress={progress}
                onClick={handlePlayToggle}
              />
            </div>
          )}
        </div>
      )}

      {heroVariant === 'simple' && (
        <div className="profile-page__hero profile-page__hero--simple">
          <img
            className="profile-page__hero-simple-img"
            src="/images/fan-hero.png"
            alt=""
          />
          <div className="profile-page__hero-overlay" />
        </div>
      )}

      {heroVariant === 'map' && (
        <div className="profile-page__hero profile-page__hero--map">
          <img
            className="profile-page__hero-map-img"
            src="/images/map-dark.png"
            alt="Location map"
          />
          <div className="profile-page__hero-overlay" />
        </div>
      )}

      <div className={`profile-page__content-body${heroVariant === 'none' || (heroVariant === 'blurred' && !heroImageUrl) ? ' profile-page__content-body--no-hero' : ''}`}>
        {children}
      </div>

      <SpecActionSheet
        open={actionSheetOpen}
        onClose={() => setActionSheetOpen(false)}
        items={actionSheetItems}
      />
    </div>
  );
};

export default ProfileLayout;
